// Batch 0 (UX/IA overhaul) — one search + filter bar for every list.
//
// The audit's most repeated P1 was "no search / filter / sort". This is the
// consistent header the enterprise lists share: a debounced search box, a row
// of typed filter <Select>s (site / NVR / status / severity …), an optional
// result count, and a "Clear" affordance when any filter is active. Purely
// controlled — the parent owns the state and does the actual filtering (or
// forwards it to the server query).
import * as React from "react";
import { SearchIcon, XIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

export interface FilterOption {
  value: string;
  label: string;
}

export interface FilterSelect {
  key: string;
  /** Placeholder shown when nothing is selected (e.g. "All NVRs"). */
  placeholder: string;
  value: string;
  options: FilterOption[];
  onChange: (value: string) => void;
}

export interface FilterBarProps {
  search?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  filters?: FilterSelect[];
  /** Rendered on the right (e.g. an "Add" button). */
  actions?: React.ReactNode;
  /** e.g. "12 of 45" — shown next to the search box. */
  resultLabel?: string;
  onClear?: () => void;
  className?: string;
}

// Sentinel for "no filter" — Radix Select disallows an empty-string value.
export const ALL = "__all__";

export function FilterBar({
  search,
  onSearchChange,
  searchPlaceholder = "Search…",
  filters = [],
  actions,
  resultLabel,
  onClear,
  className,
}: FilterBarProps) {
  const anyActive =
    (search ?? "").length > 0 ||
    filters.some((f) => f.value && f.value !== ALL);

  return (
    <div className={cn("flex flex-wrap items-center gap-2", className)}>
      {onSearchChange && (
        <div className="relative min-w-[12rem] flex-1">
          <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search ?? ""}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={searchPlaceholder}
            className="h-8 pl-8 text-xs"
          />
        </div>
      )}

      {filters.map((f) => (
        <Select key={f.key} value={f.value || ALL} onValueChange={f.onChange}>
          <SelectTrigger className="min-w-[9rem]">
            <SelectValue placeholder={f.placeholder} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{f.placeholder}</SelectItem>
            {f.options.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ))}

      {anyActive && onClear && (
        <Button variant="ghost" size="sm" onClick={onClear} className="h-8">
          <XIcon className="mr-1 h-3.5 w-3.5" />
          Clear
        </Button>
      )}

      {resultLabel && (
        <span className="text-xs text-muted-foreground">{resultLabel}</span>
      )}

      {actions && <div className="ml-auto flex gap-2">{actions}</div>}
    </div>
  );
}
